import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  deleteUser,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseAuth, firebaseDb } from '../firebase';

const AuthContext = createContext(null);

async function loadFirestoreProfile(firebaseUser) {
  if (!firebaseDb) {
    return { success: false, error: 'Firestore n’est pas configuré.' };
  }
  const ref = doc(firebaseDb, 'users', firebaseUser.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return { success: false, status: 404, error: 'Profil Firestore introuvable.' };
  }
  return {
    success: true,
    user: {
      id: firebaseUser.uid,
      uid: firebaseUser.uid,
      _id: firebaseUser.uid,
      email: firebaseUser.email?.toLowerCase().trim() || snap.data()?.email,
      ...snap.data(),
    },
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebaseAuth) {
      setLoading(false);
      return undefined;
    }

    const unsub = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await loadFirestoreProfile(fbUser);
        setUser(response.success ? response.user : null);
      } catch (e) {
        console.error('Chargement profil Firestore:', e);
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const login = async (email, password) => {
    if (!firebaseAuth) {
      return {
        success: false,
        error: 'Firebase Auth n’est pas configuré (variables REACT_APP_FIREBASE_*).',
      };
    }
    if (!firebaseDb) {
      return { success: false, error: 'Firestore est requis pour se connecter.' };
    }
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      const fbUser = firebaseAuth.currentUser;
      if (!fbUser) {
        return { success: false, error: 'Connexion Firebase incomplète.' };
      }
      const response = await loadFirestoreProfile(fbUser);
      if (!response.success) {
        await signOut(firebaseAuth);
        return {
          success: false,
          error:
            'Profil Firestore manquant. Créez un compte ou contactez le support.',
        };
      }
      setUser(response.user);
      return { success: true };
    } catch (err) {
      const code = err?.code;
      const map = {
        'auth/invalid-credential': 'Email ou mot de passe incorrect',
        'auth/user-not-found': 'Aucun utilisateur Firebase avec cet email',
        'auth/wrong-password': 'Mot de passe incorrect',
        'auth/too-many-requests': 'Trop de tentatives, réessayez plus tard',
      };
      return {
        success: false,
        error: map[code] || err.message || 'Erreur de connexion',
      };
    }
  };

  const register = async ({ email, password, firstName, lastName, phone }) => {
    if (!firebaseAuth) {
      return {
        success: false,
        error: 'Firebase Auth n’est pas configuré (variables REACT_APP_FIREBASE_*).',
      };
    }
    if (!firebaseDb) {
      return {
        success: false,
        error: 'Firestore est requis pour l’inscription (vérifiez la configuration Firebase).',
      };
    }

    let cred;
    try {
      cred = await createUserWithEmailAndPassword(firebaseAuth, email, password);

      try {
        await setDoc(doc(firebaseDb, 'users', cred.user.uid), {
          email: cred.user.email?.toLowerCase().trim(),
          firstName,
          lastName,
          phone: phone || null,
          role: 'client',
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (firestoreErr) {
        console.error('Firestore setDoc profil:', firestoreErr);
        try {
          await deleteUser(cred.user);
        } catch {
          /* ignore */
        }
        await signOut(firebaseAuth).catch(() => {});
        return {
          success: false,
          error:
            'Impossible d’enregistrer le profil (Firestore). Désactivez les bloqueurs de type « privacy » / « tracking » pour ce site, ou réessayez avec un autre navigateur.',
        };
      }

      const response = await loadFirestoreProfile(cred.user);
      if (!response.success) {
        await signOut(firebaseAuth).catch(() => {});
        return { success: false, error: response.error || 'Profil Firestore introuvable.' };
      }
      setUser(response.user);
      return { success: true };
    } catch (err) {
      if (cred?.user) {
        try {
          await deleteUser(cred.user);
        } catch {
          /* ignore */
        }
      }
      await signOut(firebaseAuth).catch(() => {});
      const code = err?.code;
      const map = {
        'auth/email-already-in-use': 'Cet email est déjà utilisé',
        'auth/weak-password': 'Mot de passe trop faible (minimum 6 caractères)',
        'auth/invalid-email': 'Email invalide',
      };
      return {
        success: false,
        error: map[code] || err.message || 'Erreur lors de l’inscription',
      };
    }
  };

  const logout = async () => {
    try {
      if (firebaseAuth) {
        await signOut(firebaseAuth);
      }
    } catch (e) {
      console.warn('signOut Firebase:', e);
    }
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    try {
      if (!firebaseAuth?.currentUser) {
        return { success: false, error: 'Non authentifié' };
      }
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }
      const uid = firebaseAuth.currentUser.uid;
      const ref = doc(firebaseDb, 'users', uid);
      await updateDoc(ref, {
        ...profileData,
        updatedAt: serverTimestamp(),
      });
      const fresh = await loadFirestoreProfile(firebaseAuth.currentUser);
      if (fresh.success) setUser(fresh.user);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      return { success: false, error: 'Erreur lors de la mise à jour' };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
